/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import { ZitadelGatewayServiceUser } from "@src/types/user-management";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";

import styles from "./gateway-service-users-list.module.scss";
import { ColoredToggleButton } from "@src/components/atoms/colored-toggle-button/colored-toggle-button";
import { changeUserStatusUseCase } from "@src/cloud-connect/user-manager/use-cases/change-user-status-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";

interface GatewayServiceUsersListProps {
  gatewayServiceUsers: ZitadelGatewayServiceUser[];
  onStatusPatched?: (userId: string, newStatus: "ACTIVE" | "INACTIVE") => void;
}

const cssClassName = "gateway-service-users-list";

export const GatewayServiceUsersList = ({
  gatewayServiceUsers,
  onStatusPatched,
}: GatewayServiceUsersListProps) => {
  const i18n = useI18N();
  const [columnWidths, setColumnWidths] = useState({
    name: 15,
    username: 15,
    status: 7,
    expirationDate: 15,
    createdAt: 15,
    updatedAt: 15,
  });

  const [gatewayServiceUsersData, setGatewayServiceUsersData] =
    useState<ZitadelGatewayServiceUser[]>(gatewayServiceUsers);

  const [resizing, setResizing] = useState(null);

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const isAllowedToChangeStatus = authContext?.permissions.includes(
    "manage-service-gateway-users",
  );

  const [isChangingStatus, setIsChangingStatus] = useState<{
    id: string;
    status: boolean;
  }>({ id: "", status: false });
  const tableRef = useRef<HTMLTableElement>(null);

  const texts = {
    nameLabel: i18n("modules.gatewayServiceUsersList.displayNameLabel"),
    usernameLabel: i18n("modules.gatewayServiceUsersList.usernameLabel"),
    statusLabel: i18n("modules.gatewayServiceUsersList.statusLabel"),
    expirationDateLabel: i18n(
      "modules.gatewayServiceUsersList.expirationDateLabel",
    ),
    statusTextActive: i18n("modules.gatewayServiceUsersList.statusTextActive"),
    statusTextInactive: i18n(
      "modules.gatewayServiceUsersList.statusTextInactive",
    ),
    errorChangeUserStatusApiText: i18n(
      "modules.gatewayServiceUsersList.errorChangeUserStatusApiText",
    ),
    noGatewayServiceUsersFoundMessage: i18n(
      "modules.gatewayServiceUsersList.noGatewayServiceUsersFoundMessage",
    ),
    createdAtLabel: i18n("modules.gatewayServiceUsersList.createdAtLabel"),
    updatedAtLabel: i18n("modules.gatewayServiceUsersList.updatedAtLabel"),
  };

  const columns = [
    { key: "name", label: texts.nameLabel },
    { key: "username", label: texts.usernameLabel },
    { key: "status", label: texts.statusLabel },
    { key: "expirationDate", label: texts.expirationDateLabel },
    { key: "createdAt", label: texts.createdAtLabel },
    { key: "updatedAt", label: texts.updatedAtLabel },
  ];

  const handleMouseDown = (e, leftColKey, rightColKey) => {
    e.preventDefault();
    e.stopPropagation();

    setResizing({
      handleKey: rightColKey,
      leftCol: leftColKey,
      rightCol: rightColKey,
      startX: e.clientX,
      startLeftWidth: columnWidths[leftColKey],
      startRightWidth: columnWidths[rightColKey],
    });
  };

  const handleMouseMove = (e) => {
    if (!resizing || !tableRef.current) return;

    const deltaX = e.clientX - resizing.startX;
    const tableWidth = tableRef.current.offsetWidth;
    const deltaPercent = (deltaX / tableWidth) * 100;

    const newLeftWidth = resizing.startLeftWidth + deltaPercent;
    const newRightWidth = resizing.startRightWidth - deltaPercent;

    if (newLeftWidth >= 5 && newRightWidth >= 5) {
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.leftCol]: newLeftWidth,
        [resizing.rightCol]: newRightWidth,
      }));
    }
  };

  const handleMouseUp = () => {
    setResizing(null);
  };

  const toggleUserStatus = async (user: ZitadelGatewayServiceUser) => {
    const previousStatus = user.status;
    const newStatus = user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setIsChangingStatus({ id: user.id, status: true });

    setGatewayServiceUsersData((prevUsers) =>
      prevUsers.map((u) =>
        u.id === user.id ? { ...u, status: newStatus } : u,
      ),
    );

    const response = await changeUserStatusUseCase(user.id, newStatus);

    if (!response.success) {
      setGatewayServiceUsersData((prevUsers) =>
        prevUsers.map((u) =>
          u.id === user.id ? { ...u, status: previousStatus } : u,
        ),
      );

      errorSnackbar(response.error || texts.errorChangeUserStatusApiText, 5000);
      console.error("error changing user status:", response.error);
    } else if (user.id) {
      onStatusPatched?.(user.id, newStatus);
    }

    setIsChangingStatus({ id: "", status: false });
  };

  const renderCellContent = (
    columnKey: string,
    user: ZitadelGatewayServiceUser,
  ) => {
    switch (columnKey) {
      case "name":
        return user.name || "";
      case "username":
        return user.username || "";
      case "status":
        return (
          <FlexRowContainer>
            <ColoredToggleButton
              disabled={!isAllowedToChangeStatus}
              isActive={user.status === "ACTIVE"}
              handleClick={() => toggleUserStatus(user)}
              variant={user.status === "ACTIVE" ? "green" : "orange"}
            />
            {isChangingStatus.status && isChangingStatus.id === user.id ? (
              <SpinnerWithVariant
                variant={[
                  "small",
                  user.status === "ACTIVE" ? "success" : "warning",
                ]}
              />
            ) : (
              <span
                className={cn(styles[`${cssClassName}__status-text`], {
                  [styles[`${cssClassName}__status-text--active`]]:
                    user.status === "ACTIVE",
                  [styles[`${cssClassName}__status-text--inactive`]]:
                    user.status === "INACTIVE",
                })}
              >
                {user.status === "ACTIVE"
                  ? texts.statusTextActive
                  : texts.statusTextInactive}
              </span>
            )}
          </FlexRowContainer>
        );
      case "expirationDate":
        return user.expiration_date ? (
          <TimezoneDate date={user.expiration_date} showRelative />
        ) : (
          "—"
        );
      case "createdAt":
        return <TimezoneDate date={user.created_at} showRelative />;
      case "updatedAt":
        return <TimezoneDate date={user.updated_at} showRelative />;
      default:
        return "";
    }
  };

  useEffect(() => {
    setGatewayServiceUsersData(gatewayServiceUsers);
  }, [gatewayServiceUsers]);

  useEffect(() => {
    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizing]);

  return (
    <div className={styles[`${cssClassName}__container`]}>
      <div className={styles[`${cssClassName}__wrapper`]}>
        {gatewayServiceUsers.length === 0 ? (
          <div className={styles[`${cssClassName}__empty-wrapper`]}>
            <p className={styles[`${cssClassName}__empty-message`]}>
              {texts.noGatewayServiceUsersFoundMessage}
            </p>
          </div>
        ) : (
          <div className={styles[`${cssClassName}__scroll`]}>
            <table ref={tableRef} className={styles[`${cssClassName}__table`]}>
              <thead>
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={column.key}
                      style={{ width: `${columnWidths[column.key]}%` }}
                      className={styles[`${cssClassName}__header`]}
                    >
                      {index > 0 ? (
                        <div
                          className={`${
                            styles[`${cssClassName}__header-resize-handle`]
                          } ${
                            resizing?.handleKey === column.key
                              ? styles[
                                  `${cssClassName}__header-resize-handle--dragging`
                                ]
                              : ""
                          }`}
                          onMouseDown={(e) =>
                            handleMouseDown(
                              e,
                              columns[index - 1].key,
                              column.key,
                            )
                          }
                        />
                      ) : (
                        <div
                          className={
                            styles[`${cssClassName}__header-static-handle`]
                          }
                        />
                      )}
                      <div
                        className={styles[`${cssClassName}__header-content`]}
                      >
                        {column.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gatewayServiceUsersData.map((gatewayServiceUser) => (
                  <tr
                    key={gatewayServiceUser.id}
                    className={styles[`${cssClassName}__row`]}
                  >
                    {columns.map((column, index) => (
                      <td
                        key={column.key}
                        style={{ width: `${columnWidths[column.key]}%` }}
                        className={styles[`${cssClassName}__cell`]}
                      >
                        <div
                          className={styles[`${cssClassName}__cell-content`]}
                        >
                          {renderCellContent(column.key, gatewayServiceUser)}
                        </div>
                        {index < columns.length - 1 && (
                          <div
                            className={
                              styles[`${cssClassName}__cell-separator`]
                            }
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
